<template>
  <v-main>
    <v-row>
      <v-col cols="12">
        <v-skeleton-loader
          v-if="loading"
          type="list-item-three-line"
          height="400"
        ></v-skeleton-loader>
        <v-card v-else>
          <v-card-item>
            <v-card-title class="text-left font-weight-light card-title-text">future net worth</v-card-title>
          </v-card-item>
          <v-divider></v-divider>
          <v-card-text class="text-left">
            <v-container>
              <v-row justify="center">
                <v-col cols="6" md="3">
                  <v-text-field
                    v-model.number="fv_expected_return"
                    label="Expected Return (%)"
                    type="number"
                    density="compact"
                    variant="outlined"
                    hide-details
                    @update:modelValue="computeFutureValueChart"
                  ></v-text-field>
                </v-col>
                <v-col cols="6" md="3">
                  <v-text-field
                    :modelValue="fv_annual_expenses_display"
                    @update:modelValue="onExpensesInput"
                    label="Annual Expenses ($)"
                    density="compact"
                    variant="outlined"
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="6" md="3">
                  <v-text-field
                    v-model.number="fv_inflation_rate"
                    label="Inflation Rate (%)"
                    type="number"
                    density="compact"
                    variant="outlined"
                    hide-details
                    @update:modelValue="computeFutureValueChart"
                  ></v-text-field>
                </v-col>
                <v-col cols="6" md="3" class="d-flex align-center">
                  <label class="v-label mr-2">Nominal</label>
                  <v-switch
                    v-model="fv_show_real"
                    label="Real"
                    density="compact"
                    hide-details
                    @update:modelValue="computeFutureValueChart"
                  ></v-switch>
                </v-col>
              </v-row>
              <FutureValueLineChart class="mt-6" v-if="gotFutureValueData" :chartData="this.future_value_chart_data"/>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-main>
</template>

<script>
import axios from 'axios';
import { mapState, mapMutations } from 'vuex';
import FutureValueLineChart from './charts/LineChart.vue';

export default {
  components: {
    FutureValueLineChart
  },
  data() {
    return {
      loading: true,
      future_value_chart_data: {
        labels: [0, 1, 5, 10, 20, 30],
        datasets: [
          {
            label: 'Future Value',
            borderColor: 'rgb(75, 192, 192)',
            tension: 0.4,
            data: []
          }
        ]
      },
      fv_expected_return: 10,
      fv_inflation_rate: 3,
      fv_show_real: false,
      fv_annual_expenses: null,
      fv_annual_salary: null,
      fv_portfolio_value: null,
      gotFutureValueData: false,
    };
  },
  async created() {
    await this.getFutureValueData();
  },
  computed: {
    ...mapState({
      token: "token"
    }),
    fv_annual_expenses_display() {
      if (this.fv_annual_expenses == null) return '';
      return this.fv_annual_expenses.toLocaleString();
    }
  },
  methods: {
    ...mapMutations(["showSnackbar"]),
    onExpensesInput(value) {
      const parsed = parseInt(String(value).replace(/,/g, ''), 10);
      this.fv_annual_expenses = isNaN(parsed) ? 0 : parsed;
      this.computeFutureValueChart();
    },
    async getFutureValueData() {
      try {
        const response = await axios.get("/reports/futurevalue", {
          headers: { Authorization: `Bearer ${this.token}` }
        });
        this.fv_portfolio_value = response.data.current_portfolio_value;
        this.fv_annual_salary = response.data.annual_salary;
        this.fv_annual_expenses = response.data.annual_expense_budget;
        this.computeFutureValueChart();
      } catch (error) {
        this.showSnackbar({ message: 'Failed to load future value data' });
      }
    },
    computeFutureValueChart() {
      const r = (this.fv_expected_return || 0) / 100;
      const inflation = (this.fv_inflation_rate || 0) / 100;
      const portfolioValue = this.fv_portfolio_value || 0;
      const salary = this.fv_annual_salary || 0;
      const expenses = this.fv_annual_expenses || 0;

      const labels = [];
      const dataPoints = [];

      let value = portfolioValue;
      for (let n = 0; n <= 30; n++) {
        labels.push(n);
        if (n === 0) {
          dataPoints.push(Math.round(value));
        } else {
          const inflatedExpenses = expenses * ((1 + inflation) ** n);
          const netContribution = salary - inflatedExpenses;
          value = value * (1 + r) + netContribution;
          const displayValue = this.fv_show_real ? value / ((1 + inflation) ** n) : value;
          dataPoints.push(Math.round(displayValue));
        }
      }

      const pointRadius = dataPoints.map((_, i) => i === dataPoints.length - 1 ? 6 : 2);
      const pointBackgroundColor = dataPoints.map(() => 'rgb(75, 192, 192)');

      this.future_value_chart_data = {
        labels,
        datasets: [
          {
            label: 'Future Value',
            borderColor: 'rgb(75, 192, 192)',
            tension: 0.4,
            data: dataPoints,
            pointRadius,
            pointBackgroundColor,
          }
        ]
      };

      this.gotFutureValueData = true;
      this.loading = false;
    },
  }
};
</script>

<style scoped>
.card-title-text {
  font-size: 2.125rem;
}
</style>
